import { Avatar, Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import EventOutlined from '@mui/icons-material/EventOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ContentCopy from '@mui/icons-material/ContentCopy';
import NotesOutlined from '@mui/icons-material/NotesOutlined';
import moment from "moment";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import Modal from "../Modal/Modal";

// The API sends the status as a boolean (true = done); older callers pass a label.
const statusLabel = (statut) => {
    if (statut === true || statut === 'true') return 'Fait';
    if (statut === false || statut === 'false') return 'À faire';
    return statut;
};

const statusStyles = [
    { match: 'termin', bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
    { match: 'fait', bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
    { match: 'cours', bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
    { match: 'annul', bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
    { match: 'faire', bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
];

const getStatusStyle = (statut) =>
    statusStyles.find((s) => (statut || '').toLowerCase().includes(s.match))
    || { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };

const isDone = (statut) => /termin|annul|fait/i.test(statut || '');

// "En retard de 3 jours", "Aujourd'hui", "Demain", "Dans 5 jours"
const getDueInfo = (dateTime, statut) => {
    if (!dateTime || isDone(statut)) return null;
    const days = moment(dateTime).startOf('day').diff(moment().startOf('day'), 'days');
    if (days < 0) return { label: `En retard de ${-days} jour${days < -1 ? 's' : ''}`, bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' };
    if (days === 0) return { label: "Aujourd'hui", bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' };
    if (days === 1) return { label: 'Demain', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' };
    return { label: `Dans ${days} jours`, bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' };
};

const initials = (name) =>
    (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

const formatDate = (value, withTime) =>
    value && moment(value).isValid() ? moment(value).format(withTime ? 'DD/MM/YYYY · HH:mm' : 'DD/MM/YYYY') : '—';

const SectionLabel = ({ children }) => (
    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.75 }}>
        {children}
    </Typography>
);

const Person = ({ label, name }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
        <Avatar sx={{ width: 36, height: 36, fontSize: '0.8rem', fontWeight: 700, bgcolor: '#ebf4fb', color: '#03407b', border: '1px solid #aed6f1' }}>
            {initials(name)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{label}</Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {name || '—'}
            </Typography>
        </Box>
    </Box>
);

const DateTile = ({ icon, label, value, highlight }) => (
    <Box sx={{
        flex: 1, display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1.25, borderRadius: '10px',
        background: highlight ? '#f0f7fd' : '#f8fafc', border: `1px solid ${highlight ? '#aed6f1' : '#e2e8f0'}`,
    }}>
        <Box sx={{ color: highlight ? '#03407b' : '#94a3b8', display: 'flex' }}>{icon}</Box>
        <Box>
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{label}</Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a' }}>{value}</Typography>
        </Box>
    </Box>
);

function ViewTacheModal({
    affecteA, affectePar, commentaire, dateTime, statut: rawStatut,
    createdAt, candidat, candidatLink, fonction, societe, societeLink,
}) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const statut = statusLabel(rawStatut);
    const note = (commentaire || '').trim();
    const statusStyle = getStatusStyle(statut);
    const due = getDueInfo(dateTime, statut);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(note);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {}
    };

    const content = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {statut && (
                    <Chip label={statut} size="small"
                        sx={{ background: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}`, fontWeight: 600, height: 24 }} />
                )}
                {due && (
                    <Chip label={due.label} size="small"
                        sx={{ background: due.bg, color: due.color, border: `1px solid ${due.border}`, fontWeight: 600, height: 24 }} />
                )}
            </Box>

            {(candidat || societe || fonction) && (
                <Box sx={{ px: 1.75, py: 1.5, borderRadius: '10px', background: '#fafcff', border: '1px solid #e2edf7' }}>
                    <SectionLabel>Concerne</SectionLabel>
                    {candidat && (
                        candidatLink
                            ? <Link to={candidatLink} onClick={handleClose} style={{ textDecoration: 'none', color: '#03407b', fontWeight: 700, fontSize: '1rem' }}>{candidat}</Link>
                            : <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>{candidat}</Typography>
                    )}
                    {(fonction || societe) && (
                        <Typography sx={{ fontSize: '0.88rem', color: '#475569', mt: 0.25 }}>
                            {fonction}
                            {fonction && societe && <Box component="span" sx={{ color: '#cbd5e1', mx: 0.75 }}>•</Box>}
                            {societe && (societeLink
                                ? <Link to={societeLink} onClick={handleClose} style={{ textDecoration: 'none', color: '#0ea5e9' }}>{societe}</Link>
                                : societe)}
                        </Typography>
                    )}
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1.25, flexDirection: { xs: 'column', sm: 'row' } }}>
                {createdAt && <DateTile icon={<EventOutlined fontSize="small" />} label="Créée le" value={formatDate(createdAt)} />}
                <DateTile icon={<ScheduleOutlined fontSize="small" />} label="À faire le" value={formatDate(dateTime, true)} highlight />
            </Box>

            <Box>
                <SectionLabel>Responsables</SectionLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' }, '& > *': { width: { xs: '100%', sm: 'auto' } } }}>
                    <Person label="Affectée par" name={affectePar} />
                    <ArrowForward sx={{ color: '#cbd5e1', fontSize: 18, display: { xs: 'none', sm: 'block' } }} />
                    <Person label="Affectée à" name={affecteA} />
                </Box>
            </Box>

            {/* Note */}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <SectionLabel>Note</SectionLabel>
                    {note && (
                        <Tooltip title={copied ? 'Copié !' : 'Copier la note'}>
                            <IconButton size="small" onClick={handleCopy} sx={{ color: '#94a3b8', mt: -0.75, '&:hover': { color: '#03407b' } }}>
                                <ContentCopy sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                {note ? (
                    <Box sx={{
                        px: 1.75, py: 1.5, borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0',
                        borderLeft: '3px solid #03407b', maxHeight: 220, overflowY: 'auto',
                        fontSize: '0.92rem', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                        {note}
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.75, py: 1.25, borderRadius: '10px', background: '#f9fafb', border: '1px dashed #e2e8f0', color: '#94a3b8' }}>
                        <NotesOutlined sx={{ fontSize: 18 }} />
                        <Typography sx={{ fontSize: '0.88rem', fontStyle: 'italic' }}>Aucune note pour cette tâche</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );

    const actions = (
        <>
            <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#475569' }}>Fermer</Button>
            {candidatLink && (
                <Button component={Link} to={candidatLink} onClick={handleClose} variant="contained" endIcon={<ArrowForward />}
                    sx={{ textTransform: 'none', background: '#03407b', boxShadow: 'none', '&:hover': { background: '#02357a', boxShadow: 'none' } }}>
                    Voir le candidat
                </Button>
            )}
        </>
    );

    return (
        <>
            <Tooltip title="Voir le détail">
                <IconButton size="small" onClick={handleOpen} color="primary">
                    <Visibility />
                </IconButton>
            </Tooltip>
            <Modal
                open={open}
                onClose={handleClose}
                title="Détails de la tâche"
                maxWidth="sm"
                actions={actions}
            >
                {content}
            </Modal>
        </>
    );
}

export default ViewTacheModal;
